import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Eye, ExternalLink, Loader2, Lock, Monitor, RefreshCw, Smartphone, Tablet } from "lucide-react";
import { errMsg, useMe } from "../lib/api";
import SitePreview from "../pages/dashboard/SitePreview";
import { UnderstandingCard } from "./UnderstandingCard";

const WIDTH = { desktop: "100%", tablet: "768px", mobile: "375px" };

export function DeviceFrame({ device, children, tall = false, frameRef = null }) {
  const small = typeof window !== "undefined" && window.innerWidth < 768;
  return (
    <div ref={frameRef} className="bg-[#e8e4d8] rounded-xl p-1.5 sm:p-3 overflow-auto" style={{ maxHeight: small ? "none" : tall ? "calc(100vh - 120px)" : 720 }}>
      <div className="mx-auto bg-white shadow-lg overflow-hidden transition-[width] duration-300" style={{ width: WIDTH[device], maxWidth: "100%", borderRadius: device === "desktop" ? 6 : 18 }} data-testid={`preview-frame-${device}`}>{children}</div>
    </div>);
}

export function DeviceSwitch({ device, setDevice }) {
  return (
    <div className="flex rounded-full border bg-white p-1" role="tablist" aria-label="Device preview">
      {[["desktop", Monitor], ["tablet", Tablet], ["mobile", Smartphone]].map(([k, I]) => <button key={k} role="tab" aria-selected={device === k} onClick={() => setDevice(k)} className={`px-3 py-1.5 rounded-full text-xs inline-flex items-center gap-1 capitalize transition-colors ${device === k ? "bg-[#0b3d2e] text-white" : "text-slate-600"}`} data-testid={`device-${k}`}><I className="w-3.5 h-3.5" />{k}</button>)}
    </div>);
}

const pageSpec = (spec, page) => {
  if (page === "home") return spec;
  const s = spec.content.sections.find((x) => x.id === page);
  return { ...spec, layout: { ...spec.layout, hero: "centered", sections_order: [page] },
    content: { ...spec.content, hero: { title: s.title || s.type, subtitle: "", cta: spec.content.hero.cta }, sections: [{ ...s, title: "" }] } };
};

export function PreviewViewer({ spec, edit = null, tall = false }) {
  const [device, setDevice] = useState(() => (window.innerWidth < 768 ? "mobile" : "desktop"));
  const [page, setPage] = useState("home");
  const [jump, setJump] = useState(null);
  const frame = useRef(null);
  const pages = [{ id: "home", title: "Home" }, ...spec.content.sections.filter((s) => s.type !== "cta").map((s) => ({ id: s.id, title: s.title || s.type }))];
  const onNav = (id) => { const pg = id.replace(/^sec-/, ""); if (pages.some((x) => x.id === pg)) { setPage(pg); frame.current?.scrollTo({ top: 0 }); } else { setPage("home"); setJump(id); } };
  useEffect(() => {
    if (!jump) return;
    const el = frame.current?.querySelector(`[id="${jump}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setJump(null);
  }, [jump, page]);
  const pick = (id) => { setPage(id); frame.current?.scrollTo({ top: 0 }); };
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-1.5" data-testid="preview-pages" aria-label="Preview pages">
          {pages.map((pg) => <button key={pg.id} onClick={() => pick(pg.id)} className={`chip ${page === pg.id ? "chip-on" : ""}`} data-testid={`preview-chip-${pg.id}`}>{pg.title.length > 24 ? `${pg.title.slice(0, 24)}…` : pg.title}</button>)}
        </div>
        <DeviceSwitch device={device} setDevice={setDevice} />
      </div>
      {edit && page !== "home" && <p className="text-xs text-amber-800" data-testid="edit-home-only">Editing works on the Home view. <button className="underline" onClick={() => setPage("home")}>Go to Home</button></p>}
      <DeviceFrame device={device} tall={tall} frameRef={frame}><SitePreview spec={pageSpec(spec, page)} device={device} edit={page === "home" ? edit : null} onNav={onNav} navSections={page === "home" ? null : spec.content.sections.filter((s) => s.title)} /></DeviceFrame>
    </div>);
}

export default function WebsitePreview({ project, api, auto = false, onChange }) {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const fired = useRef(false);
  const me = useMe()?.me;
  const v2 = me?.builder_v2;
  const load = useCallback(() => api.get(`/projects/${project.id}/design`).then((r) => { setSt(r.data); return r.data; }), [api, project.id]);
  const generate = useCallback(async () => {
    setBusy(true);
    try { await api.post(`/projects/${project.id}/design/generate`); await load(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  }, [api, project.id, load]);
  useEffect(() => { load().catch(() => {}); }, [load]);
  useEffect(() => {
    if (!st) return undefined;
    if (st.job?.status === "running") { const t = setTimeout(() => load().then((d) => { if (d.job?.status !== "running") onChange?.(); }), 3000); return () => clearTimeout(t); }
    const left = st.preview_limit - st.previews_used;
    if (auto && me && !v2 && !fired.current && st.preview_mode && left > 0 && st.job?.status !== "failed" && (!st.current_id || st.stale)) { fired.current = true; generate(); }
    return undefined;
  }, [st, auto, load, generate, onChange, me, v2]);
  if (!st) return <div className="card-lux text-sm text-slate-500">Loading your website preview…</div>;
  const shown = st.versions.find((v) => v.id === st.current_id);
  const running = st.job?.status === "running";
  const left = Math.max(0, st.preview_limit - st.previews_used);
  return (
    <section className="card-lux !p-5 sm:!p-7 space-y-4" data-testid="website-preview">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="eyebrow flex items-center gap-1.5"><Eye className="w-3.5 h-3.5" /> Read-only</p>
          <h3 className="mt-1 text-2xl text-[#062a20]" data-testid="website-preview-label">Website Preview — Editing unlocks after advance payment</h3>
          <p className="text-xs text-slate-500 mt-1">An AI design concept based on your requirements — not a finished website. After your advance is verified you can change themes, colours, layouts and content, and request revisions.</p>
        </div>
        <span className="text-[11px] px-2.5 py-1 rounded-full bg-[#f4efe2] text-[#7a5a15] inline-flex items-center gap-1" data-testid="preview-credits"><Lock className="w-3 h-3" />{left} of {st.preview_limit} free previews left</span>
        {v2 && shown && <a href={`/preview/${project.id}`} target="_blank" rel="noreferrer" className="btn-gold !py-2 inline-flex items-center gap-2" data-testid="preview-open-tab"><ExternalLink className="w-4 h-4" />Open preview in new tab</a>}
      </div>
      {running && <div className="rounded-xl bg-[#0b3d2e] text-[#f6ecd2] px-4 py-3 text-sm flex items-center gap-2" data-testid="preview-generating"><Loader2 className="w-4 h-4 animate-spin" /> Creating your website preview — usually under a minute. You can keep reviewing your quotation.</div>}
      {st.job?.status === "failed" && !running && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm flex flex-wrap items-center justify-between gap-2" data-testid="preview-failed">
          <span className="text-red-800">{st.job.error}</span>
          {left > 0 && <button onClick={generate} disabled={busy} className="btn-emerald !py-1.5" data-testid="preview-retry-btn"><RefreshCw className="w-4 h-4" /> Try again</button>}
        </div>)}
      {st.stale && !running && shown && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm flex flex-wrap items-center justify-between gap-2" data-testid="preview-stale">
          <span className="text-amber-900">Your requirements changed since this preview was made.{left === 0 ? " You've used all free previews; your quotation is still up to date." : ""}</span>
          {left > 0 && <button onClick={generate} disabled={busy} className="btn-emerald !py-1.5" data-testid="preview-refresh-btn"><RefreshCw className="w-4 h-4" /> Refresh preview ({left} left)</button>}
        </div>)}
      {v2 && <UnderstandingCard api={api} pid={project.id} locked={st.design_approved} defaultOpen={!shown} onSaved={() => load()} />}
      {shown ? <PreviewViewer spec={shown.spec} key={shown.id} /> : !running && (
        <div className="text-sm text-slate-600 flex flex-wrap items-center gap-3" data-testid="preview-empty">
          <span>See how your website could look before you pay.</span>
          {left > 0 ? <button onClick={generate} disabled={busy} className="btn-gold !py-2" data-testid="preview-generate-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />} Create my preview</button>
            : <span className="text-xs text-slate-500">No free previews left for this project.</span>}
        </div>)}
    </section>);
}
